import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Avatar,
    Container,
    useTheme,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import { listEncountersByPatient, createEncounter } from '../../../api/encounter';
import { apiGet } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

const NAVY = '#052F5D';

function formatDate(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function relativeFrom(d) {
    if (!d) return '';
    const days = Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.round(days / 30)} months ago`;
    return `${Math.round(days / 365)} years ago`;
}

function ageFromDob(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
}

function initialsOf(p) {
    if (!p) return '·';
    const a = (p.fName || '')[0]; const b = (p.lName || '')[0];
    return `${a || ''}${b || ''}`.toUpperCase() || '·';
}

function StatCard({ kicker, value, sub }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                borderColor: 'rgba(5,47,93,0.10)',
                bgcolor: '#fff',
                height: '100%',
            }}
        >
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, fontSize: 10.5 }}>
                {kicker}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: NAVY, lineHeight: 1.1, mt: 0.25 }}>
                {value}
            </Typography>
            {sub && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {sub}
                </Typography>
            )}
        </Paper>
    );
}

export default function PatientEncounters() {
    const { patientId, role: roleParam } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { canWrite } = usePermissions();
    const canCreate = canWrite('encounters');

    const [items, setItems] = useState([]);
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [encRes, patRes] = await Promise.all([
                listEncountersByPatient(patientId),
                // /api/get-patient-information/:currentBranch is the list
                // endpoint for a branch — not the per-patient detail. Use
                // get-patient-data/basicInformation which returns the
                // patient + emergency-contact merged in one call.
                apiGet(`api/get-patient-data/basicInformation/${patientId}`).catch(() => null),
            ]);
            setItems(encRes.data || []);
            const p = patRes?.patientData || patRes?.data || patRes || null;
            setPatient(p);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

    const newEncounter = async () => {
        try {
            setCreating(true);
            const r = await createEncounter(patientId);
            const id = r.data?._id;
            navigate(`/${roleParam || 'admin'}/patients/${patientId}/encounters/${id}`);
        } finally {
            setCreating(false);
        }
    };

    const stats = useMemo(() => {
        const last = items[0];
        const finalized = items.filter((e) => e.status === 'finalized').length;
        const drafts = items.filter((e) => e.status === 'draft').length;
        const primaryDx = (last?.diagnoses || []).find((d) => d.primary) || (last?.diagnoses || [])[0];
        return { total: items.length, finalized, drafts, last, primaryDx };
    }, [items]);

    const age = ageFromDob(patient?.dob);

    return (
        <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
            <Button
                onClick={() => navigate(`/${roleParam || 'admin'}/patient-management`)}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500, mb: 1.5 }}
            >
                Back to patients
            </Button>

            {/* Patient header — mirrors the encounter editor banner so the UX feels continuous */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 2px rgba(5,47,93,0.04)',
                }}
            >
                <Avatar sx={{ bgcolor: NAVY, color: '#fff', width: 52, height: 52, fontSize: 18, fontWeight: 600 }}>
                    {initialsOf(patient)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
                        Patient
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: NAVY, lineHeight: 1.2 }}>
                        {patient ? `${patient.fName || ''} ${patient.lName || ''}`.trim() || '—' : 'Loading…'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        MRN {patient?.mrn || '—'}
                        <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>·</Box>
                        {patient?.gender || '—'}
                        <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>·</Box>
                        {age !== null ? `${age} yrs` : '— yrs'}
                    </Typography>
                </Box>
                <Button
                    onClick={() => navigate(`/${roleParam || 'admin'}/patients/${patientId}/documents`)}
                    startIcon={<FolderOpenRoundedIcon />}
                    sx={{ textTransform: 'none', fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}
                >
                    Documents
                </Button>
                {/* Greyed out (not hidden) for read-only roles so the
                    capability is discoverable and the tooltip explains why. */}
                <Tooltip title={canCreate ? '' : 'You have read-only access to encounters.'} arrow>
                    <span>
                        <Button
                            onClick={newEncounter}
                            disabled={creating || !canCreate}
                            variant="contained"
                            startIcon={<AddRoundedIcon />}
                            sx={{
                                bgcolor: NAVY,
                                boxShadow: 'none',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                                '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                            }}
                        >
                            {creating ? 'Creating…' : 'New encounter'}
                        </Button>
                    </span>
                </Tooltip>
            </Paper>

            {/* Stats row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                    <StatCard kicker="Total encounters" value={loading ? '…' : stats.total} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard kicker="Finalized" value={loading ? '…' : stats.finalized} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard kicker="Drafts" value={loading ? '…' : stats.drafts} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard
                        kicker="Last visit"
                        value={loading ? '…' : (stats.last ? formatDate(stats.last.visitDate) : '—')}
                        sub={stats.last ? relativeFrom(stats.last.visitDate) : 'No prior visits'}
                    />
                </Grid>
            </Grid>

            {/* Encounter list */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: { xs: 1, md: 1.5 },
                    bgcolor: '#fff',
                }}
            >
                {loading ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <CircularProgress size={26} sx={{ color: NAVY }} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box
                        sx={{
                            p: 5,
                            textAlign: 'center',
                            border: '1px dashed rgba(5,47,93,0.15)',
                            borderRadius: 2,
                            bgcolor: 'rgba(5,47,93,0.025)',
                        }}
                    >
                        <EventNoteRoundedIcon sx={{ fontSize: 36, color: NAVY, opacity: 0.4 }} />
                        <Typography sx={{ mt: 1, color: NAVY, fontWeight: 600 }}>No encounters yet</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {canCreate
                                ? 'Start the patient\'s record by creating the first encounter.'
                                : 'A clinician will create the first encounter.'}
                        </Typography>
                        {canCreate && (
                            <Button
                                onClick={newEncounter}
                                disabled={creating}
                                variant="contained"
                                startIcon={<AddRoundedIcon />}
                                sx={{
                                    mt: 2, bgcolor: NAVY, boxShadow: 'none',
                                    textTransform: 'none', fontWeight: 600,
                                    '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                                }}
                            >
                                Create first encounter
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Stack divider={<Divider sx={{ borderColor: 'rgba(5,47,93,0.06)' }} />}>
                        {items.map((e) => {
                            const isFin = e.status === 'finalized';
                            // A signed visit reopened for correction shows as
                            // "Amending" rather than "Finalized" — the record
                            // is unlocked, and the chart should say so.
                            const isAmending = (e.amendments || []).some((a) => !a.closedAt);
                            const primary = (e.diagnoses || []).find((d) => d.primary) || (e.diagnoses || [])[0];
                            const dxCount = (e.diagnoses || []).length;
                            // Rendered twice: inline next to the date on phones,
                            // in the trailing action group on desktop.
                            const statusChip = (
                                <Chip
                                    size="small"
                                    icon={isFin && !isAmending ? <LockOutlinedIcon sx={{ fontSize: 13 }} /> : <EditOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label={isAmending ? 'Amending' : isFin ? 'Finalized' : 'Draft'}
                                    sx={{
                                        bgcolor: isAmending ? '#FFF6E5' : isFin ? 'rgba(46,125,50,0.10)' : 'rgba(5,47,93,0.08)',
                                        color: isAmending ? '#5C3A00' : isFin ? 'success.dark' : NAVY,
                                        border: '1px solid',
                                        borderColor: isAmending ? '#F5C26B' : isFin ? 'rgba(46,125,50,0.25)' : 'rgba(5,47,93,0.15)',
                                        fontWeight: 600,
                                        '.MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                            );
                            return (
                                <Box
                                    key={e._id}
                                    onClick={() => navigate(`/${roleParam || 'admin'}/patients/${patientId}/encounters/${e._id}`)}
                                    sx={{
                                        p: { xs: 1.5, md: 2 },
                                        borderRadius: 1.5,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        // Phones get a stacked card; md+ keeps the original
                                        // single-line row untouched.
                                        alignItems: { xs: 'stretch', md: 'center' },
                                        gap: { xs: 1.5, md: 2 },
                                        transition: 'background-color 140ms ease',
                                        '&:hover': { bgcolor: 'rgba(5,47,93,0.035)' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 6, alignSelf: 'stretch', borderRadius: 999,
                                            flexShrink: 0,
                                            bgcolor: isFin ? 'success.main' : 'rgba(5,47,93,0.25)',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            alignItems: { xs: 'stretch', md: 'center' },
                                            gap: { xs: 0.5, md: 2 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: { md: 110 },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 14 }}>
                                                    {formatDate(e.visitDate)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {formatDateTime(e.visitDate).split(', ').pop()}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
                                                {statusChip}
                                            </Box>
                                        </Box>
                                        <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
                                            <Typography
                                                sx={{ fontSize: 14, color: 'text.primary', fontWeight: 500, whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                                                noWrap
                                            >
                                                {e.chiefComplaint || (
                                                    <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                                        No chief complaint recorded
                                                    </Box>
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                                                noWrap
                                            >
                                                {primary
                                                    ? <>Dx: {(primary.code || primary.icd10Code) ? <><strong>{primary.code || primary.icd10Code}</strong>{' '}</> : null}{primary.description} {dxCount > 1 ? `· +${dxCount - 1} more` : ''}</>
                                                    : 'No diagnosis yet'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        {statusChip}
                                        <Tooltip title="Open" arrow>
                                            <IconButton size="small" sx={{ color: 'text.disabled' }}>
                                                <ChevronRightRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Paper>
        </Container>
    );
}
